// Main.ts
import * as THREE from 'three';
import { Group } from '@tweenjs/tween.js';
import GameController from './scenes/controllers/GameController';
import AssetManager from './managers/AssetManager';
import setupRenderer from './core/Renderer';
import setupCamera from './core/Camera';
import { GarageUI } from './ui/GarageUI';
import { GameplayUI } from './ui/GameplayUI';
import GameScene from './scenes/GameScene';
import GarageScene from './scenes/GarageScene';
import BaseEnv from './envs/BaseEnv';
import EnvManager from './managers/EnvManager';
import socketService from './SocketService';
import { GameEvents } from '../shared/constants';
import { getSocketConfig } from '../utils/socketUrl';

declare global {
  interface Window {
    AssetManager: any;
  }
}

window.AssetManager = AssetManager;

export function initializeGame(
  container: HTMLElement,
  username: string,
  selectedSkin: string,
  roomId?: string,
  progressCallback: ((p: number, m?: string) => void) | null = null,
  existingSocket: any = null
) {
  const socketConfig = getSocketConfig();
  
  if (existingSocket) {
    socketService.connect(socketConfig, existingSocket);
    if (progressCallback) progressCallback(20, 'Using existing connection');
  } else if (socketService.socket && socketService.socket.connected) {
    if (progressCallback) progressCallback(20, 'Reusing existing connection');
  } else {
    socketService.connect(socketConfig);
  }

  return new Promise<any>((resolve) => {
    const socket = socketService.socket as any;

    const proceedWithGameInit = async () => {
      if (roomId) {
        if (progressCallback) progressCallback(30, 'Joining game room');
        try {
          let resp: any;
          if (typeof (socket as any).emitWithAck === 'function') {
            resp = await (socket as any).emitWithAck(GameEvents.ROOM_JOIN, roomId);
          } else {
            resp = await new Promise((resolve, reject) => {
              const t = setTimeout(() => reject(new Error('timeout')), 5000);
              (socket as any).emit(GameEvents.ROOM_JOIN, roomId, (r: any) => { clearTimeout(t); resolve(r); });
            });
          }
          if (resp && resp.ok) {
            if (progressCallback) progressCallback(40, 'Room joined successfully');
            if (progressCallback) progressCallback(50, 'Starting game...');
            startGame();
          } else {
            if (progressCallback) progressCallback(50, 'Room join failed, starting offline');
            startGame();
          }
        } catch (e) {
          if (progressCallback) progressCallback(50, 'Room join failed, starting offline');
          startGame();
        }
      } else {
        startGame();
      }
    };

    if (socket.connected) {
      if (progressCallback) progressCallback(20, 'Connected to server');
      proceedWithGameInit();
    } else {
      socket.on('connect', () => {
        if (progressCallback) progressCallback(20, 'Connected to server');
        proceedWithGameInit();
      });
    }

    const startGame = () => {
      const clock = new THREE.Clock();
      const camera = setupCamera();
      const renderer = setupRenderer(container);
      const gameScene = new GameScene(camera, GameplayUI);
      const garageScene = new GarageScene(camera, GarageUI, renderer);
      const tweenGroup = new Group();
      const envManager = new EnvManager(renderer, camera);
      const game = new GameController(
        camera as any,
        gameScene as any,
        tweenGroup,
        envManager,
        username,
        selectedSkin
      );
      const garageEnv = new BaseEnv(garageScene, camera, renderer);
      const gameplayEnv = new BaseEnv(gameScene, camera, renderer, game);
      envManager.addEnvironment('garage', garageEnv);
      envManager.addEnvironment('game', gameplayEnv);

      const handleResize = () => {
        (camera as any).aspect = window.innerWidth / window.innerHeight;
        (camera as any).updateProjectionMatrix();
        renderer.setSize(window.innerWidth, window.innerHeight);
      };
      window.addEventListener('resize', handleResize, false);

      if (progressCallback) progressCallback(60, 'Loading game assets...');
      const assetInfo = AssetManager.getLoadedAssetsInfo();
      const launch = () => {
        if (progressCallback) progressCallback(80, 'Assets loaded, initializing game...');
        game.initGame();
        gameScene.initScene();
        envManager.switchEnvironment('game');
        (camera as any).add(AssetManager.audioListener);
        if (progressCallback) progressCallback(90, 'Game initialized, starting...');
        function animate(time?: number) {
          requestAnimationFrame(animate);
          tweenGroup.update();
          const deltaTime = clock.getDelta();
          envManager.updateWithRender(deltaTime);
          if (time && time % 3000 < 16) {
            // console.log('Game animation running, deltaTime:', deltaTime);
          }
        }
        animate();
        if (progressCallback) progressCallback(100, 'Game ready!');
        resolve({ game, envManager, renderer, handleResize });
      };

      // Always ensure full asset pack is preloaded. Some textures may be
      // partially present (e.g., skins loaded in Garage), which would make
      // textureCount > 0 but miss required gameplay assets (brickColorMap, etc.).
      AssetManager.setOnLoadCallback(() => launch());
      AssetManager.preloadAssets().catch((err: any) => console.error('Failed to preload assets:', err));
    };

    
  });
}
